import {
  Group,
  Loader,
  Stack,
  Text
} from "@mantine/core";
import { toast } from "sonner";
import { useIntegrationApp } from "@membranehq/react";
import { IconPlus, IconRefresh, IconTrash } from "@tabler/icons-react";
import { useState, useMemo, useEffect, useRef } from "react";
import { createBulkFieldMappings, deleteFieldMapping, updateFieldMapping } from "../../../../../services/syncConfiguration/syncConfigurationEndpoints";
import { useUpdateSyncConfiguration, useFieldMappings } from "../../../../../hooks/QueryHooks";
import { useQueryClient } from "@tanstack/react-query";
import type { FieldDefinition, HubSpotField } from "../../../../../services/syncConfiguration/syncConfigurationEndpoints.interface";
import useWorkspaceStore from "../../../../../store/workspace/workspaceStore";
import { Button as DsButton } from "../../../../../components/ui/button";
import { Checkbox as DsCheckbox } from "../../../../../components/ui/checkbox";
import { SearchableMenuSelect } from "../../../../../components/ui/searchable-menu-select";
import type { WritebackConfig, WritebackFieldMapping } from "../types";

interface WriteBackConfigurationProps {
  writebackConfig: WritebackConfig;
  onChange: (config: WritebackConfig) => void;
  workspaceFields?: FieldDefinition[];
  hubspotFields?: HubSpotField[];
  connectionId?: string;
  syncId?: string; // This should be the writeback sync ID
  slug?: string; // Object slug for fieldMapping key (same as write-in)
  onRefreshFields?: () => void;
  providerLabel?: string;
  hideHeader?: boolean;
  persistenceMode?: "component" | "managed";
  managedMappings?: WritebackFieldMapping[];
  onManagedMappingsChange?: (mappings: WritebackFieldMapping[], source?: "hydrate" | "user") => void;
  reservedProviderFields?: Set<string>;
}

interface LabeledCheckboxProps {
  checked: boolean;
  label: string;
  description?: string;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}

const LabeledCheckbox = ({
  checked,
  label,
  description,
  disabled,
  onChange,
}: LabeledCheckboxProps) => (
  <label
    className={`flex items-start gap-2 ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
  >
    <DsCheckbox
      checked={checked}
      disabled={disabled}
      onCheckedChange={(value) => onChange(value === true)}
      className="mt-1 h-4 w-4"
    />
    <span className="space-y-0.5">
      <Text fw={500}>{label}</Text>
      {description ? <Text size="sm" c="dimmed">{description}</Text> : null}
    </span>
  </label>
);

const WriteBackConfiguration = ({
  writebackConfig,
  onChange,
  workspaceFields = [],
  hubspotFields = [],
  connectionId,
  syncId,
  slug,
  onRefreshFields,
  providerLabel = 'HubSpot',
  hideHeader = false,
  persistenceMode = "component",
  managedMappings,
  onManagedMappingsChange,
  reservedProviderFields = new Set(),
}: WriteBackConfigurationProps) => {
  const workspace: any = useWorkspaceStore((s: any) => s.workspace);
  const integrationApp = useIntegrationApp();
  const queryClient = useQueryClient();
  const rowInputClass = "h-9 w-full rounded-[5px] border border-border-chip bg-background px-3 text-[13px] text-foreground";
  const [savingMappings, setSavingMappings] = useState(false);
  const [savingRecordActions, setSavingRecordActions] = useState(false);
  const [refreshingFields, setRefreshingFields] = useState(false);
  const updateSyncConfigMutation = useUpdateSyncConfiguration();

  // Handle refresh fields with loading state
  const handleRefreshFields = async () => {
    setRefreshingFields(true);

    try {
      if (onRefreshFields) {
        // Call parent refresh to refresh provider fields from integration.app
        await Promise.resolve(onRefreshFields());

        toast.success('Success', {
          description: `${providerLabel} fields refreshed successfully`,
        });
      } else {
        toast.info('Info', {
          description: 'No refresh function available',
        });
      }
    } catch (error) {
      console.error(`Error refreshing ${providerLabel} fields:`, error);
      toast.error('Error', {
        description: `Failed to refresh ${providerLabel} fields`,
      });
    } finally {
      setRefreshingFields(false);
    }
  };

  // Use React Query to fetch field mappings
  const { data: fieldMappings, refetch: refetchFieldMappings } = useFieldMappings({
    workspaceId: workspace?.id || '',
    syncId: syncId || '',
    enabled: !!workspace?.id && !!syncId && writebackConfig.enabled
  });

  // Create mapping IDs lookup from React Query data
  const existingMappingIds = useMemo(() => {
    if (!fieldMappings || !Array.isArray(fieldMappings)) return {};

    const mappingIds: Record<string, string> = {};
    fieldMappings.forEach((mapping: any) => {
      if (mapping.source_field_name && mapping.id) {
        mappingIds[mapping.source_field_name] = mapping.id;
      }
    });
    return mappingIds;
  }, [fieldMappings]);

  // Transform React Query field mappings to UI format
  const uiFieldMappings: WritebackFieldMapping[] = useMemo(() => {
    if (!fieldMappings || !Array.isArray(fieldMappings)) return [];

    return fieldMappings
      .filter((mapping: any) => !mapping.is_owner_assignment) // Exclude role mappings
      .map((mapping: any): WritebackFieldMapping => {
        // Check if this is a metric field by checking for destination_metric_name
        const isMetricField = mapping.destination_field_type === 'metric' || !!mapping.destination_metric_name;

        let hyperengageFieldId = '';

        if (isMetricField && mapping.destination_metric_name) {
          // For metric fields, find the field by column_name (destination_metric_name)
          const metricField = workspaceFields.find(f => f.column_name === mapping.destination_metric_name);
          hyperengageFieldId = metricField?.id || '';
        } else {
          // For regular fields, use destination_field_id
          hyperengageFieldId = mapping.destination_field_id || '';
        }

        return {
          hyperengage_field: hyperengageFieldId,
          hubspot_field: mapping.source_field_name, // HubSpot field name
          field_type: mapping.destination_field_type || "field",
          is_required: mapping.is_required || false,
          needs_creation: false, // Assume existing fields don't need creation
          metric_aggregation_type: isMetricField ? (mapping.metric_aggregation_type || 'value') : undefined
        };
      });
  }, [fieldMappings, workspaceFields]);

  // Local state for managing field mappings UI changes
  const [localFieldMappings, setLocalFieldMappings] = useState<WritebackFieldMapping[]>([]);
  const managedMappingsHydratedRef = useRef(false);

  const setDraftMappings = (nextMappings: WritebackFieldMapping[]) => {
    if (persistenceMode === "managed") {
      onManagedMappingsChange?.(nextMappings, "user");
      return;
    }
    setLocalFieldMappings(nextMappings);
  };

  // Combine API data with local state changes
  const currentFieldMappings: WritebackFieldMapping[] = useMemo(() => {
    if (persistenceMode === "managed") {
      if (Array.isArray(managedMappings)) return managedMappings;
      return uiFieldMappings;
    }

    // If local state has data, use it (user has made changes)
    if (localFieldMappings.length > 0) {
      return localFieldMappings;
    }
    // Otherwise use API data
    return uiFieldMappings;
  }, [persistenceMode, managedMappings, localFieldMappings, uiFieldMappings]);

  // Update local state when API data changes
  useEffect(() => {
    if (persistenceMode === "managed") {
      managedMappingsHydratedRef.current = false;
    }
  }, [persistenceMode, syncId]);

  // Update local state when API data changes
  useEffect(() => {
    if (persistenceMode === "managed") {
      // Wait until the managed field-mappings query has resolved once for this sync.
      if (typeof fieldMappings === "undefined") return;
      if (managedMappingsHydratedRef.current) return;

      if (uiFieldMappings.length > 0 && (!managedMappings || managedMappings.length === 0)) {
        onManagedMappingsChange?.(uiFieldMappings, "hydrate");
      }
      managedMappingsHydratedRef.current = true;
      return;
    }

    if (uiFieldMappings.length > 0 && localFieldMappings.length === 0) {
      setLocalFieldMappings(uiFieldMappings);
    }
  }, [
    persistenceMode,
    fieldMappings,
    uiFieldMappings,
    localFieldMappings.length,
    managedMappings,
    onManagedMappingsChange,
  ]);

  // Update parent component when field mappings change for validation
  // Note: We don't store fields in writebackConfig since it's not part of the type
  // The validation will be handled by the parent component through field mapping APIs

  // Delete a specific field mapping
  const deleteWritebackFieldMapping = async (hubspotField: string) => {
    if (!workspace?.id || !syncId) {
      return;
    }

    const mappingId = existingMappingIds[hubspotField];
    if (!mappingId) {
      return;
    }

    try {
      await deleteFieldMapping({
        workspace_id: workspace.id,
        sync_id: syncId,
        mapping_id: mappingId
      });

      // Invalidate field mappings query to refresh data
      await queryClient.invalidateQueries({
        queryKey: ["field-mappings", workspace.id, syncId]
      });
    } catch (error: any) {
      // 404 errors are expected when mappings are already deleted
      if (error?.response?.status === 404) {
        // Invalidate field mappings query to refresh data even on 404
        await queryClient.invalidateQueries({
          queryKey: ["field-mappings", workspace.id, syncId]
        });
      } else {
        console.error(`Failed to delete field mapping for ${hubspotField}:`, error);
        throw error;
      }
    }
  };

  // Save record actions to sync configuration
  const saveRecordActions = async (allow_creates: boolean, allow_updates: boolean) => {
    if (!workspace?.id || !syncId) {
      toast.error('Error', {
        description: !syncId ? 'Writeback sync is still being created. Please wait a moment and try again.' : 'Unable to save record actions - missing configuration',
      });
      return;
    }

    setSavingRecordActions(true);

    try {
      await updateSyncConfigMutation.mutateAsync({
        workspace_id: workspace.id,
        sync_id: syncId,
        data: {
          allow_creates,
          allow_updates,
        }
      });

      toast.success('Success', {
        description: 'Record actions saved successfully',
      });
    } catch (error) {
      console.error('Error saving record actions:', error);
      toast.error('Error', {
        description: 'Failed to save record actions',
      });
    } finally {
      setSavingRecordActions(false);
    }
  };

  // Save writeback field mappings to database and integration.app with proper update/delete/create logic
  const saveWriteBackMappings = async () => {
    if (!workspace?.id || !syncId || !connectionId) {
      toast.error('Error', {
        description: !syncId ? 'Writeback sync is still being created. Please wait a moment and try again.' : 'Missing required configuration to save mappings',
      });
      return;
    }

    const validMappings: WritebackFieldMapping[] = currentFieldMappings.filter(
      (mapping: WritebackFieldMapping) => mapping.hyperengage_field && mapping.hubspot_field
    );

    if (validMappings.length === 0) {
      // Auto-disable writeback when no fields are mapped
      onChange({
        enabled: false,
        allow_creates: writebackConfig.allow_creates || false,
        allow_updates: writebackConfig.allow_updates || false,
      });

      toast.warning('Writeback Disabled', {
        description: 'Writeback sync has been disabled since no fields are mapped',
      });
      return;
    }

    setSavingMappings(true);

    // Show loading notification
    toast.loading('Saving writeback mappings...', {
      id: 'saving-writeback-mappings',
      description: 'Updating writeback field mappings in database and integration',
    });

    try {
      // Refetch existing mappings to get current state
      const freshFieldMappings = await refetchFieldMappings();

      // Build fresh mapping IDs from the refetched data
      const freshMappingIds: Record<string, string> = {};
      if (freshFieldMappings.data && Array.isArray(freshFieldMappings.data)) {
        freshFieldMappings.data.forEach((mapping: any) => {
          if (mapping.source_field_name && mapping.id) {
            freshMappingIds[mapping.source_field_name] = mapping.id;
          }
        });
      }

      const updatePromises: Promise<any>[] = [];
      const createPromises: Promise<any>[] = [];
      const deletePromises: Promise<any>[] = [];
      const failedUpdateMappings: any[] = [];

      // Prepare all mapping data
      const allDbMappings = validMappings.map((mapping: WritebackFieldMapping) => {
        const hyperengageField = workspaceFields.find(f => f.id === mapping.hyperengage_field);
        const isMetricField = hyperengageField?.attribute_type === 'metric_traits';

        const fieldMappingData: any = {
          workspace_id: workspace.id,
          sync_configuration_id: syncId,
          source_field_name: mapping.hubspot_field, // Source is always HubSpot
          source_field_id: mapping.hubspot_field,
          destination_field_type: isMetricField ? "metric" : "field",
          is_required: mapping.is_required || false,
          transform_rules: {},
          is_owner_assignment: false
        };

        if (isMetricField) {
          // For metric fields, set destination_metric_name instead of destination_field_id
          fieldMappingData.destination_metric_name = hyperengageField?.column_name || "";
          fieldMappingData.destination_field_id = null;
          fieldMappingData.metric_aggregation_type = mapping.metric_aggregation_type || 'value';
        } else {
          // For regular fields, set destination_field_id (UUID)
          fieldMappingData.destination_field_id = mapping.hyperengage_field; // Already UUID
        }

        return fieldMappingData;
      });

      // Handle updates and creates
      allDbMappings.forEach((mapping: any) => {
        const existingMappingId = freshMappingIds[mapping.source_field_name];

        if (existingMappingId) {
          // Update existing mapping
          updatePromises.push(
            updateFieldMapping({
              workspace_id: workspace.id,
              sync_id: syncId,
              mapping_id: existingMappingId,
              data: mapping
            }).catch(error => {
              // 404 errors indicate the mapping no longer exists, treat as create instead
              if (error?.response?.status === 404) {
                failedUpdateMappings.push(mapping);
              } else {
                throw error;
              }
            })
          );
        } else {
          // Create new mapping
          createPromises.push(Promise.resolve(mapping));
        }
      });

      // Handle deletions - find mappings that were removed
      const currentHubSpotFields = new Set(validMappings.map((m: any) => m.hubspot_field));

      Object.entries(freshMappingIds).forEach(([hubspotField, mappingId]) => {
        if (!currentHubSpotFields.has(hubspotField)) {
          // This mapping was removed, delete it
          deletePromises.push(
            deleteFieldMapping({
              workspace_id: workspace.id,
              sync_id: syncId,
              mapping_id: mappingId
            }).catch(error => {
              // 404 errors are expected when mappings are already deleted
              if (error?.response?.status !== 404) {
                throw error;
              }
            })
          );
        }
      });

      // Execute updates and deletions
      await Promise.all([...updatePromises, ...deletePromises]);

      // Create new mappings (including failed updates)
      const allNewMappings = [...createPromises.map(p => p), ...failedUpdateMappings];
      if (allNewMappings.length > 0) {
        const resolvedMappings = await Promise.all(createPromises);
        const finalMappings = [...resolvedMappings, ...failedUpdateMappings];

        if (finalMappings.length > 0) {
          await createBulkFieldMappings({
            workspace_id: workspace.id,
            sync_id: syncId,
            mappings: finalMappings
          });
        }
      }

      // Invalidate field mappings query to refresh data
      await queryClient.invalidateQueries({
        queryKey: ["field-mappings", workspace.id, syncId]
      });

      // Build Integration.app exportValue for writeback (opposite direction of importValue)
      const exportValue: Record<string, any> = {};
      validMappings.forEach((mapping: WritebackFieldMapping) => {
        const hyperengageField = workspaceFields.find(f => f.id === mapping.hyperengage_field);
        const fieldColumnName = hyperengageField?.column_name || mapping.hyperengage_field;

        exportValue[mapping.hubspot_field] = {
          $var: `$.${fieldColumnName}` // Use column_name for Integration.app
        };
      });

      // Save to Integration.app as exportValue for writeback sync
      // Use same fieldMapping key as write-in (object slug)
      const fieldMappingKey = slug || 'contacts';
      await integrationApp
        .connection(connectionId)
        .fieldMapping(fieldMappingKey)
        .patch({
          exportValue
        });

      // Show success notification
      toast.success('Writeback mappings saved!', {
        id: 'saving-writeback-mappings',
        description: `Successfully saved ${validMappings.length} writeback field mappings`,
      });
    } catch (error) {
      console.error('Error saving writeback field mappings:', error);

      // Show error notification
      toast.error('Failed to save writeback mappings', {
        id: 'saving-writeback-mappings',
        description: 'An error occurred while saving writeback mappings. Please try again.',
      });
    } finally {
      setSavingMappings(false);
    }
  };
  const handleAddField = () => {
    const newField: WritebackFieldMapping = {
      hyperengage_field: "",
      hubspot_field: "",
      field_type: "text",
      is_required: false,
      needs_creation: false,
      metric_aggregation_type: 'value', // Default to value
    };

    setDraftMappings([...currentFieldMappings, newField]);
  };

  const handleRemoveField = async (index: number) => {
    const fieldToRemove = currentFieldMappings?.[index];
    const newFields: WritebackFieldMapping[] = currentFieldMappings.filter((_: WritebackFieldMapping, i: number) => i !== index);

    if (persistenceMode === "managed") {
      setDraftMappings(newFields);
      return;
    }

    // Delete from database and integration.app if it exists
    if (fieldToRemove?.hubspot_field) {
      const mappingId = existingMappingIds[fieldToRemove.hubspot_field];

      if (mappingId) {
        // This mapping exists in database, delete it
        try {
          await deleteWritebackFieldMapping(fieldToRemove.hubspot_field);
        } catch (error) {
          toast.error('Error', {
            description: 'Failed to delete field mapping',
          });
          return; // Don't update local state if delete failed
        }
      } else {
        // This mapping is only in local state (unsaved), just remove from UI
      }
    }

    // Update local state
    setDraftMappings(newFields);

    // Auto-disable writeback if no valid fields remain
    const validFields = newFields.filter((field: WritebackFieldMapping) => field.hyperengage_field && field.hubspot_field);
    const shouldDisable = validFields.length === 0;

    if (shouldDisable && writebackConfig.enabled) {
      onChange({
        enabled: false,
        allow_creates: writebackConfig.allow_creates || false,
        allow_updates: writebackConfig.allow_updates || false,
      });
    }

    if (shouldDisable && writebackConfig.enabled) {
      toast.warning('Writeback Disabled', {
        description: 'Writeback sync has been disabled since no fields are mapped',
      });
    }
  };

  const handleFieldChange = (index: number, updates: Partial<WritebackFieldMapping>) => {
    const newFields = [...currentFieldMappings];
    newFields[index] = { ...newFields[index], ...updates };
    setDraftMappings(newFields);
  };

  // Filter HubSpot fields to only show writable fields for write-back
  const writableHubSpotFields = hubspotFields.filter(field => !field.readOnly && field.key && field.title);

  const getCompatibleHubSpotFields = (hyperengageField?: FieldDefinition) => {
    if (!hyperengageField) return writableHubSpotFields;

    // Filter HubSpot fields based on data type compatibility
    const compatibleFields = writableHubSpotFields.filter(hubspotField => {
      const heType = hyperengageField.data_type?.toLowerCase();
      const hsType = hubspotField.type?.toLowerCase();
      const hsFormat = (hubspotField as any).format?.toLowerCase(); // Handle format field

      // Text/String compatibility
      if (heType === 'text' && ['string', 'enumeration', 'text'].includes(hsType)) return true;
      if (heType === 'string' && ['string', 'enumeration', 'text'].includes(hsType)) return true;

      // Number compatibility: HubSpot number fields can map to both int and float in HyperEngage
      if (heType === 'int' && ['number', 'integer', 'float', 'decimal'].includes(hsType)) return true;
      if (heType === 'float' && ['number', 'integer', 'float', 'decimal'].includes(hsType)) return true;
      if (heType === 'integer' && ['number', 'integer', 'float', 'decimal'].includes(hsType)) return true;
      if (heType === 'number' && ['number', 'integer', 'float', 'decimal'].includes(hsType)) return true;

      // Boolean compatibility
      if (heType === 'boolean' && hsType === 'boolean') return true;

      // Date/DateTime compatibility: Check both type and format
      if (heType === 'datetime') {
        if (['datetime', 'date', 'timestamp'].includes(hsType)) return true;
        if (hsType === 'string' && hsFormat === 'date-time') return true; // HubSpot date fields often have type:"string" + format:"date-time"
      }
      if (heType === 'date') {
        if (['datetime', 'date', 'timestamp'].includes(hsType)) return true;
        if (hsType === 'string' && hsFormat === 'date-time') return true;
      }

      // JSON compatibility
      if (heType === 'json' && ['object', 'array'].includes(hsType)) return true;

      // Fallback: allow all string-like fields for text types
      if (['text', 'string'].includes(heType) && ['string', 'enumeration', 'text'].includes(hsType)) return true;

      return false;
    });

    // If no compatible fields found, return all writable fields (user can decide)
    if (compatibleFields.length === 0) {
      return writableHubSpotFields;
    }

    return compatibleFields;
  };

  const getReservedHubSpotFieldsForRow = (rowIndex: number): Set<string> => {
    const reserved = new Set<string>();

    currentFieldMappings.forEach((mapping, index) => {
      if (index === rowIndex) return;
      if (mapping.hubspot_field) {
        reserved.add(mapping.hubspot_field);
      }
    });

    reservedProviderFields.forEach((fieldKey) => {
      reserved.add(fieldKey);
    });

    return reserved;
  };

  if (!writebackConfig.enabled) return null;
  if (persistenceMode === "component" && !syncId) return null;

  return (
    <Stack gap="sm">

      {/* Record Actions Configuration */}
      <div>
        <Stack gap="md">
          <Group>
            <LabeledCheckbox
              label="Create and update"
              description={`Create new records in ${providerLabel} when they don't exist`}
              checked={writebackConfig.allow_creates || false}
              disabled={persistenceMode === "component" ? (savingRecordActions || !syncId) : false}
              onChange={(newValue) => {
                // Update local state first
                onChange({
                  ...writebackConfig,
                  allow_creates: newValue,
                });
                if (persistenceMode === "component") {
                  // Save to backend (don't await to avoid ESLint warning)
                  saveRecordActions(newValue, writebackConfig.allow_updates || false).catch(console.error);
                }
              }}
            />
          </Group>
          <Group>
            <LabeledCheckbox
              label="Update Only"
              description={`Update existing records in ${providerLabel}`}
              checked={writebackConfig.allow_updates || false}
              disabled={persistenceMode === "component" ? (savingRecordActions || !syncId) : false}
              onChange={(newValue) => {
                // Update local state first
                onChange({
                  ...writebackConfig,
                  allow_updates: newValue,
                });
                if (persistenceMode === "component") {
                  // Save to backend (don't await to avoid ESLint warning)
                  saveRecordActions(writebackConfig.allow_creates || false, newValue).catch(console.error);
                }
              }}
            />
          </Group>
        </Stack>
      </div>

      {/* Field Mappings */}
      <>
        {!hideHeader && (
          <Group justify="space-between">
            <Text fw={500} size="sm">Map Hyperengage fields to {providerLabel}</Text>
            <Group>
              <DsButton
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => { handleRefreshFields().catch(console.error); }}
                disabled={refreshingFields || !onRefreshFields}
                title="Refresh fields"
                aria-label="Refresh fields"
              >
                <IconRefresh size="0.75rem" className={refreshingFields ? "animate-spin" : undefined} />
              </DsButton>
            </Group>
          </Group>
        )}

        {hideHeader && onRefreshFields && (
          <Group justify="flex-end">
            <DsButton
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => { handleRefreshFields().catch(console.error); }}
              disabled={refreshingFields}
              title="Refresh fields"
              aria-label="Refresh fields"
            >
              <IconRefresh size="0.75rem" className={refreshingFields ? "animate-spin" : undefined} />
            </DsButton>
          </Group>
        )}

        {!hideHeader && (
          <Text size="xs" c="dimmed">
            Map Hyperengage fields to {providerLabel} fields for write-back sync.
          </Text>
        )}

        {refreshingFields ? (
          <Group justify="center" p="md">
            <Loader size="sm" />
            <Text size="xs">Refreshing {providerLabel} fields...</Text>
          </Group>
        ) : (
          <div className="space-y-2">
            {currentFieldMappings.length > 0 && (
              <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_32px] items-center gap-3">
                <Text size="xs" fw={600} c="dimmed" className="uppercase tracking-wide">Hyperengage Column</Text>
                <Text size="xs" fw={600} c="dimmed" className="uppercase tracking-wide">{providerLabel} Field</Text>
                <span />
              </div>
            )}

            {currentFieldMappings.map((fieldMapping, index) => {
              const hyperengageField = workspaceFields.find(f => f.id === fieldMapping.hyperengage_field);
              const compatibleFields = getCompatibleHubSpotFields(hyperengageField);
              const reservedHubSpotFields = getReservedHubSpotFieldsForRow(index);
              const hyperengageOptions = workspaceFields.map((field) => ({
                value: field.id,
                label: `${field.display_name || field.column_name} (${field.data_type})`,
                searchText: `${field.display_name || ""} ${field.column_name || ""} ${field.data_type || ""}`,
              }));
              const providerOptions = compatibleFields
                .filter((field) => {
                  if (field.key === fieldMapping.hubspot_field) return true;
                  return !reservedHubSpotFields.has(field.key);
                })
                .map((field) => ({
                  value: field.key,
                  label: `${field.title} (${field.type})`,
                  searchText: `${field.key} ${field.type || ""}`,
                }));

              return (
                <div
                  key={`writeback-${fieldMapping.hyperengage_field || 'new'}-${fieldMapping.hubspot_field || 'unmapped'}`}
                  className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_32px] items-center gap-3"
                >
                  <SearchableMenuSelect
                    value={fieldMapping.hyperengage_field || ""}
                    options={[
                      { value: "", label: "Select Hyperengage column...", searchText: "clear unselect" },
                      ...hyperengageOptions,
                    ]}
                    placeholder="Select Hyperengage column..."
                    onChange={(value) => {
                      const selectedField = workspaceFields.find((field) => field.id === value);
                      const isSelectedMetric = selectedField?.attribute_type === "metric_traits";
                      handleFieldChange(index, {
                        hyperengage_field: value,
                        field_type: selectedField?.data_type || "text",
                        hubspot_field: "",
                        needs_creation: false,
                        metric_aggregation_type: isSelectedMetric ? "value" : undefined,
                      });
                    }}
                    className={rowInputClass}
                  />

                  <SearchableMenuSelect
                    value={fieldMapping.hubspot_field || ""}
                    options={[
                      { value: "", label: `Select ${providerLabel} field...`, searchText: "clear unselect" },
                      ...providerOptions,
                    ]}
                    placeholder={`Select ${providerLabel} field...`}
                    onChange={(value) => {
                      handleFieldChange(index, {
                        hubspot_field: value,
                        needs_creation: false,
                      });
                    }}
                    className={rowInputClass}
                  />

                  <button
                    type="button"
                    onClick={() => { handleRemoveField(index).catch(console.error); }}
                    className="h-9 w-8 rounded flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-status-cancelled transition-colors"
                    aria-label="Remove writeback mapping row"
                  >
                    <IconTrash size="0.75rem" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div className="pt-2">
          <DsButton
            type="button"
            variant="outline"
            size="sm"
            className="h-8 text-[12px]"
            onClick={handleAddField}
          >
            <IconPlus size="0.75rem" className="mr-1.5" />
            Add a trait
          </DsButton>
        </div>

        {persistenceMode === "component" && currentFieldMappings.length > 0 && (
          <Group justify="flex-end">
            <DsButton
              type="button"
              size="sm"
              className="h-8 text-[12px]"
              onClick={() => { saveWriteBackMappings().catch(console.error); }}
              disabled={currentFieldMappings.length === 0 || !syncId || savingMappings}
            >
              {savingMappings
                ? "Saving..."
                : !syncId
                  ? "Creating Sync..."
                  : "Save Mappings"}
            </DsButton>
          </Group>
        )}
      </>
    </Stack>
  );
};

export default WriteBackConfiguration;
